// Insurance tab rebuild — per-claim detail drawer. Claim metadata +
// vertical status timeline + "Advance" to the next legal stage
// (Filed→Acknowledged→Adjusting→Settled|Denied; gated Insurance.Verify).
// Supporting documents live on the loan Documents tab — no per-claim
// attachment mechanism exists.

import { useState } from "react";
import { Badge, Button } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { ApiError } from "@/api/client";
import { useAdvanceInsuranceClaim } from "@/hooks/portfolioAdmin/useInsuranceClaims";
import { ResourceDrawer } from "../common/ResourceDrawer";
import { formatCalendarDate } from "@/design-system/lib/format";
import { ClaimStatusTimeline } from "./ClaimStatusTimeline";
import {
  CLAIM_STATUS_BADGE,
  formatClaimAmount,
  nextStagesOf,
} from "./claimLifecycle";
import type {
  InsuranceClaimDto,
  InsuranceClaimStatus,
} from "@/api/portfolioAdmin/insuranceClaimsApi";

interface ClaimDrawerProps {
  loanId: string;
  claim: InsuranceClaimDto;
  open: boolean;
  onClose: () => void;
}

const ADVANCE_LABEL: Record<InsuranceClaimStatus, string> = {
  Filed: "File",
  Acknowledged: "Mark acknowledged",
  Adjusting: "Begin adjusting",
  Settled: "Mark settled",
  Denied: "Mark denied",
};

export function ClaimDrawer({ loanId, claim, open, onClose }: ClaimDrawerProps) {
  const toast = useToast();
  const auth = useAuth();
  const canAdvance =
    auth.hasPermission("Insurance.Verify") || auth.hasRoleCode("ADMIN");

  const advance = useAdvanceInsuranceClaim(loanId);
  const nextStages = nextStagesOf(claim.status);

  const [amountSettled, setAmountSettled] = useState("");
  const [notes, setNotes] = useState("");

  const onAdvance = async (status: InsuranceClaimStatus) => {
    if (status === "Settled" && !amountSettled) {
      toast.error("Settled amount is required to mark the claim settled.");
      return;
    }
    try {
      await advance.mutateAsync({
        claimId: claim.id,
        body: {
          status,
          amountSettled: status === "Settled" ? Number(amountSettled) : null,
          notes: notes.trim() || null,
        },
      });
      toast.success(`Claim advanced to ${status}.`);
      setNotes("");
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : err instanceof Error
            ? err.message
            : "Could not advance claim.",
      );
    }
  };

  return (
    <ResourceDrawer
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={`${claim.policyType} claim`}
      subtitle={`Claim #${claim.claimNumber ?? "—"} · Loss ${formatCalendarDate(claim.dateOfLoss)}`}
      testId="claim-drawer"
      footer={<Button variant="secondary" onClick={onClose}>Close</Button>}
    >
      <div className="flex flex-col gap-space-4" data-testid="claim-drawer-body">
        <section>
          <h3 className="font-display font-semibold text-h4 m-0 mb-space-2">
            Claim detail
          </h3>
          <dl className="grid grid-cols-2 gap-y-space-1 text-small m-0">
            <dt className="text-caption text-gray-mid uppercase tracking-label">Status</dt>
            <dd className="m-0">
              <Badge status={CLAIM_STATUS_BADGE[claim.status]}>{claim.status}</Badge>
            </dd>
            <dt className="text-caption text-gray-mid uppercase tracking-label">Date of loss</dt>
            <dd className="m-0">{formatCalendarDate(claim.dateOfLoss)}</dd>
            <dt className="text-caption text-gray-mid uppercase tracking-label">Claim #</dt>
            <dd className="m-0 font-mono">{claim.claimNumber ?? "—"}</dd>
            <dt className="text-caption text-gray-mid uppercase tracking-label">Amount claimed</dt>
            <dd className="m-0 font-mono">{formatClaimAmount(claim.amountClaimed)}</dd>
            <dt className="text-caption text-gray-mid uppercase tracking-label">Amount settled</dt>
            <dd className="m-0 font-mono">{formatClaimAmount(claim.amountSettled)}</dd>
          </dl>
          <p className="text-small text-onyx mt-space-2 mb-0">{claim.description}</p>
          {claim.notes && (
            <p className="text-caption text-gray-mid mt-space-1 mb-0">
              Notes: {claim.notes}
            </p>
          )}
        </section>

        <section>
          <h3 className="font-display font-semibold text-h4 m-0 mb-space-2">
            Status timeline
          </h3>
          <ClaimStatusTimeline claim={claim} />
        </section>

        {canAdvance && nextStages.length > 0 && (
          <section data-testid="claim-advance-section">
            <h3 className="font-display font-semibold text-h4 m-0 mb-space-2">
              Advance claim
            </h3>
            <div className="flex flex-col gap-space-2">
              {nextStages.includes("Settled") && (
                <label className="flex flex-col gap-space-1 text-caption">
                  <span className="text-gray-mid uppercase tracking-label">
                    Settled amount ($) <span className="text-status-error">*</span>
                  </span>
                  <input
                    type="number"
                    className="h-9 px-space-2 border border-border-light rounded-md text-small"
                    value={amountSettled}
                    onChange={(e) => setAmountSettled(e.target.value)}
                    data-testid="claim-advance-amountSettled"
                  />
                </label>
              )}
              <label className="flex flex-col gap-space-1 text-caption">
                <span className="text-gray-mid uppercase tracking-label">Notes</span>
                <textarea
                  rows={3}
                  className="w-full border border-border-light rounded-md p-space-2 text-small"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  data-testid="claim-advance-notes"
                />
              </label>
              <div className="flex items-center gap-space-2">
                {nextStages.map((stage) => (
                  <Button
                    key={stage}
                    variant={stage === "Denied" ? "secondary" : "primary"}
                    size="sm"
                    onClick={() => void onAdvance(stage)}
                    disabled={advance.isPending}
                    data-testid={`claim-advance-${stage}`}
                  >
                    {ADVANCE_LABEL[stage]}
                  </Button>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="rounded-md bg-white-warm p-space-2">
          <p className="text-caption text-gray-mid m-0">
            Attach supporting documents in the Documents tab.
          </p>
        </section>
      </div>
    </ResourceDrawer>
  );
}
